<script lang="ts" setup>
import { Studio } from '@/modules/Studios/types'
import { computed } from 'vue'
import { mdiEmailOutline, mdiFacebook, mdiInstagram, mdiLink, mdiTwitter, mdiYoutube } from '@mdi/js'

const props = defineProps<{
  studio: Studio
  isLoading: boolean
}>()

const emit = defineEmits(['update'])

// Regular expressions for validating full URLs
const facebookRegex = /^(https?:\/\/)?(www\.)?facebook\.com\/(.+)/i
const instagramRegex = /^(https?:\/\/)?(www\.)?instagram\.com\/(.+)/i
const twitterRegex = /^(https?:\/\/)?(www\.)?twitter\.com\/(.+)/i
const youtubeRegex = /^(https?:\/\/)?(www\.)?youtube\.com\/channel\/(.+)/i
const websiteRegex = /^(https?:\/\/)(.+)/i

// Computed properties to handle both full URLs and usernames
const facebookValue = computed({
  get: () => {
    if (!props.studio.socials.facebook) return ''
    const match = props.studio.socials.facebook.match(facebookRegex)
    return match ? match[3] : props.studio.socials.facebook
  },
  set: (value) => {
    if (!value) {
      props.studio.socials.facebook = ''
    } else {
      props.studio.socials.facebook = value
    }
  },
})

const instagramValue = computed({
  get: () => {
    if (!props.studio.socials.instagram) return ''
    const match = props.studio.socials.instagram.match(instagramRegex)
    return match ? match[3] : props.studio.socials.instagram
  },
  set: (value) => {
    if (!value) {
      props.studio.socials.instagram = ''
    } else {
      props.studio.socials.instagram = value
    }
  },
})

const twitterValue = computed({
  get: () => {
    if (!props.studio.socials.twitter) return ''
    const match = props.studio.socials.twitter.match(twitterRegex)
    return match ? match[3] : props.studio.socials.twitter
  },
  set: (value) => {
    if (!value) {
      props.studio.socials.twitter = ''
    } else {
      props.studio.socials.twitter = value
    }
  },
})

const youtubeValue = computed({
  get: () => {
    if (!props.studio.socials.youtube) return ''
    const match = props.studio.socials.youtube.match(youtubeRegex)
    return match ? match[3] : props.studio.socials.youtube
  },
  set: (value) => {
    if (!value) {
      props.studio.socials.youtube = ''
    } else {
      props.studio.socials.youtube = value
    }
  },
})

const websiteValue = computed({
  get: () => {
    if (!props.studio.socials.website) return ''
    const match = props.studio.socials.website.match(websiteRegex)
    return match ? match[2] : props.studio.socials.website
  },
  set: (value) => {
    if (!value) {
      props.studio.socials.website = ''
    } else {
      props.studio.socials.website = value
    }
  },
})

// Function to format the final URLs when saving
const formatSocialUrls = () => {
  // Format Facebook URL
  if (props.studio.socials.facebook) {
    if (!facebookRegex.test(props.studio.socials.facebook)) {
      props.studio.socials.facebook = `https://www.facebook.com/${props.studio.socials.facebook}`
    }
  }

  // Format Instagram URL
  if (props.studio.socials.instagram) {
    if (!instagramRegex.test(props.studio.socials.instagram)) {
      props.studio.socials.instagram = `https://www.instagram.com/${props.studio.socials.instagram}`
    }
  }

  // Format Twitter URL
  if (props.studio.socials.twitter) {
    if (!twitterRegex.test(props.studio.socials.twitter)) {
      props.studio.socials.twitter = `https://twitter.com/${props.studio.socials.twitter}`
    }
  }

  // Format YouTube URL
  if (props.studio.socials.youtube) {
    if (!youtubeRegex.test(props.studio.socials.youtube)) {
      props.studio.socials.youtube = `https://www.youtube.com/channel/${props.studio.socials.youtube}`
    }
  }

  // Format Website URL
  if (props.studio.socials.website) {
    if (!websiteRegex.test(props.studio.socials.website)) {
      props.studio.socials.website = `https://${props.studio.socials.website}`
    }
  }

  // Emit update event
  emit('update')
}
</script>

<template>
  <div class="settings-socials">
    <div class="fields">
      <div class="field">
        <v-text-field
          v-model="studio.socials.email"
          autocomplete="off"
          background-color="#fff"
          hide-details
          placeholder="Add an email"
          outlined
          :append-icon="mdiEmailOutline"
        />
      </div>

      <div class="field">
        <v-text-field
          v-model="websiteValue"
          autocomplete="off"
          background-color="#fff"
          hide-details
          placeholder="Add a website"
          outlined
          :append-icon="mdiLink"
        >
          <span
            slot="prepend-inner"
            class="prefix"
          >
            https://
          </span>
        </v-text-field>
      </div>

      <div class="field">
        <v-text-field
          v-model="facebookValue"
          autocomplete="off"
          background-color="#fff"
          hide-details
          placeholder="Facebook handle"
          outlined
          :append-icon="mdiFacebook"
        >
          <span
            slot="prepend-inner"
            class="prefix"
          >
            https://www.facebook.com/
          </span>
        </v-text-field>
      </div>
      <div class="field">
        <v-text-field
          v-model="instagramValue"
          autocomplete="off"
          background-color="#fff"
          hide-details
          placeholder="Instagram handle"
          outlined
          :append-icon="mdiInstagram"
        >
          <span
            slot="prepend-inner"
            class="prefix"
          >
            https://www.instagram.com/
          </span>
        </v-text-field>
      </div>

      <div class="field">
        <v-text-field
          v-model="youtubeValue"
          autocomplete="off"
          background-color="#fff"
          hide-details
          placeholder="Channel ID"
          outlined
          :append-icon="mdiYoutube"
        >
          <span
            slot="prepend-inner"
            class="prefix"
          >
            https://www.youtube.com/channel/
          </span>
        </v-text-field>
      </div>

      <div class="field">
        <v-text-field
          v-model="twitterValue"
          autocomplete="off"
          background-color="#fff"
          hide-details
          placeholder="Twitter handle"
          outlined
          :append-icon="mdiTwitter"
        >
          <span
            slot="prepend-inner"
            class="prefix"
          >
            https://twitter.com/
          </span>
        </v-text-field>
      </div>
    </div>

    <div class="options">
      <v-btn
        :loading="isLoading"
        color="var(--color-blue-800)"
        dark
        height="52"
        width="180"
        @click="formatSocialUrls"
      >
        Save
      </v-btn>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.settings-socials {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
}

.fields {
  padding: 20px;

  .field {
    margin-bottom: 16px;
  }

  .prefix {
    margin-top: 3px;
    margin-right: -4px;
  }
}

.options {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding: 20px;
  border-top: 1px solid var(--color-grey-600);
}
</style>
